/**
 * Keep subscriber identifiers out of the model call.
 *
 * A tenant's own ICCIDs and MSISDNs are their data, and the assistant does not
 * need them to reason: "SUB-3 is burning data" is exactly as useful to the
 * model as the 19-digit number, and only one of the two leaves our
 * infrastructure. The mapping is per-request and lives on the stack — nothing
 * is cached at module scope, because a Worker isolate is reused across tenants.
 *
 * The label is restored in the answer, so the user still reads the real ICCID.
 * That restoration has to work on a token stream, where a label can be split
 * across chunks, which is what `LabelRestorer` is for.
 */

/** ICCIDs are 18-22 digits; MSISDNs 8-15, optionally with a leading +. */
const IDENTIFIER = /\b\+?\d{8,22}\b/g;

export interface Pseudonymized {
  text: string;
  /** label -> original identifier. Per request. Never persisted. */
  map: Map<string, string>;
}

/**
 * Replace every long digit run with a stable per-request label.
 *
 * Stable matters: the same ICCID mentioned in three facts must get the same
 * label, or the model will reason about three different subscribers.
 */
export function pseudonymize(text: string, seed?: Map<string, string>): Pseudonymized {
  const map = new Map<string, string>(seed);
  const byOriginal = new Map<string, string>();
  for (const [label, original] of map) byOriginal.set(original, label);

  const out = text.replace(IDENTIFIER, (match) => {
    const existing = byOriginal.get(match);
    if (existing) return existing;
    const label = `SUB-${map.size + 1}`;
    map.set(label, match);
    byOriginal.set(match, label);
    return label;
  });

  return { text: out, map };
}

/**
 * Longest label first.
 *
 * "SUB-1" is a prefix of "SUB-12", so replacing in insertion order turns
 * "SUB-12" into an ICCID with a stray "2" glued on — a corrupted identifier
 * that still looks like a plausible one.
 */
function byLengthDesc(map: Map<string, string>): Array<[string, string]> {
  return [...map].sort((a, b) => b[0].length - a[0].length);
}

/** One-shot restore, for non-streaming answers. */
export function restore(text: string, map: Map<string, string>): string {
  let out = text;
  for (const [label, original] of byLengthDesc(map)) {
    out = out.split(label).join(original);
  }
  return out;
}

/**
 * Restores labels across a token stream.
 *
 * A model emits "SUB" then "-12" in separate chunks often enough that a naive
 * per-chunk replace silently ships raw labels to the reader. This holds back
 * any trailing text that could still grow into a label, and releases it as soon
 * as it cannot.
 */
export class LabelRestorer {
  private pending = "";

  constructor(private readonly map: Map<string, string>) {}

  /** Feed a chunk, get back the text that is safe to emit now. */
  push(chunk: string): string {
    this.pending += chunk;
    if (this.map.size === 0) {
      const out = this.pending;
      this.pending = "";
      return out;
    }

    // Holdback is computed on the RAW text, before substitution. Substituting
    // first would rewrite "SUB-1" the moment it arrived, and the "2" of a
    // "SUB-12" landing in the next chunk would then trail a full ICCID.
    const hold = this.holdbackLength(this.pending);
    const cut = this.pending.length - hold;
    const safe = this.pending.slice(0, cut);
    this.pending = this.pending.slice(cut);

    return this.substitute(safe);
  }

  /** Flush whatever is left when the stream ends. */
  end(): string {
    const text = this.pending;
    this.pending = "";
    return this.substitute(text);
  }

  private substitute(text: string): string {
    return restore(text, this.map);
  }

  /**
   * Longest suffix of `text` that is a PROPER prefix of some label.
   *
   * Proper is what makes SUB-1 / SUB-12 work: "SUB-1" is a proper prefix of
   * "SUB-12", so it is held rather than substituted, and a "2" arriving next
   * still completes the longer label.
   */
  private holdbackLength(text: string): number {
    let longest = 0;
    for (const label of this.map.keys()) {
      const max = Math.min(label.length - 1, text.length);
      for (let n = max; n > longest; n--) {
        if (text.endsWith(label.slice(0, n))) {
          longest = n;
          break;
        }
      }
    }
    return longest;
  }
}
